type CacheEntry = {
  data: unknown;
  /** `${generation}:${depsKey}` when the entry was written. */
  stamp: string;
};

const store = new Map<string, CacheEntry>();
const listeners = new Map<string, Set<() => void>>();
/** Dedupes concurrent loaders that share a cache key (nav + layout + page). */
const inflight = new Map<string, Promise<unknown>>();

export function cacheGet<T>(key: string): CacheEntry & { data: T } | undefined {
  const entry = store.get(key);
  return entry as (CacheEntry & { data: T }) | undefined;
}

export function cacheSet<T>(key: string, data: T, stamp: string): void {
  store.set(key, { data, stamp });
  listeners.get(key)?.forEach((fn) => fn());
}

export function cacheClear(prefix?: string): void {
  if (!prefix) {
    store.clear();
    listeners.forEach((set) => set.forEach((fn) => fn()));
    return;
  }
  for (const key of [...store.keys()]) {
    if (key.startsWith(prefix)) {
      store.delete(key);
      listeners.get(key)?.forEach((fn) => fn());
    }
  }
}

export function cacheSubscribe(key: string, fn: () => void): () => void {
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(fn);
  return () => {
    set!.delete(fn);
    if (set!.size === 0) listeners.delete(key);
  };
}

/** Stable cache keys for shared workspace data. */
export function collabsCacheKey(
  sessionId: string | null | undefined,
  activeCreatorId?: string | null,
): string | null {
  if (!sessionId) return null;
  return `collabs:${sessionId}:${activeCreatorId ?? "personal"}`;
}

/** Run `loader` once per key while in flight — concurrent callers share the promise. */
export function cacheLoad<T>(key: string, loader: () => Promise<T>): Promise<T> {
  const existing = inflight.get(key);
  if (existing) return existing as Promise<T>;
  const promise = loader().finally(() => {
    if (inflight.get(key) === promise) inflight.delete(key);
  });
  inflight.set(key, promise);
  return promise;
}
